import React from "react";
import { useTranslation } from "react-i18next";

import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import ButtonVariant from "app/components/Button/ButtonVariant";

import styles from "./styles.module.scss";

type PropsDef = {
  isOpen: boolean;
  onlinePaymentType?: string;
  chargeTiming?: string;
  currencySymbol?: string;
  sessionPrice?: number;
  sessionCount?: number;
  onCancel: () => void;
  onConfirm: () => void;
};

// CN-2515: creating a repeating series with online payment shows this popup so
// the coach understands when money actually moves. The copy varies by the
// charge-timing choice:
//   - immediate      → only the first session is billed now, the rest after each
//   - all_immediate  → one up-front bill for the whole series (price x sessions)
//   - after_session  → nothing now; each session is billed overnight after it runs
// The `confirm_cancel` wrapper class keeps clicks inside the popup (and on its
// overlay) off FullCalendar's unselect path (see UNSELECT_CANCEL in the calendar
// views) — without it, pointerup unselects the date selection and unmounts the
// form before the Confirm click lands.
export default function BillNowConfirmationModal({
  isOpen,
  onlinePaymentType,
  chargeTiming,
  currencySymbol,
  sessionPrice,
  sessionCount,
  onCancel,
  onConfirm,
}: PropsDef) {
  const { t } = useTranslation();

  const isCardOnFile = onlinePaymentType === 'card_on_file';
  const timing = chargeTiming ?? 'immediate';
  const seriesTotal = (Number(sessionPrice) || 0) * (sessionCount || 0);
  const amount = `${currencySymbol ?? ''}${seriesTotal}`;

  const title = isCardOnFile
    ? t("Charging for recurring sessions")
    : t("Invoicing for recurring sessions");

  let message: string;
  if (isCardOnFile) {
    if (timing === 'all_immediate') {
      message = t("Charge All Now charges the card once for all repeating sessions. This charge will be for {{amount}}. Once the repeating sessions are created, you can individually edit each event.", { amount });
    } else if (timing === 'after_session') {
      message = t("Charge After Session charges the card after each session occurs. Charges are processed overnight following each session. Once the repeating sessions are created, you can individually edit each event.");
    } else {
      message = t("Charge Now charges the card for the first session. Subsequent sessions will be charged after the session occurs. Once the repeating sessions are created, you can individually edit each event.");
    }
  } else if (timing === 'all_immediate') {
    message = t("Send All Invoices Now sends one invoice for all the repeating sessions. This invoice will be for {{amount}}. Once the repeating sessions are created, you can individually edit each event.", { amount });
  } else if (timing === 'after_session') {
    message = t("Send Invoice After Session sends an invoice after each session occurs. Invoices are sent overnight following each session. Pending invoices are not created. Once the repeating sessions are created, you can individually edit each event.");
  } else {
    message = t("Send Invoice Now sends an invoice for the first session. Subsequent sessions will be invoiced after the session occurs. Once the repeating sessions are created, you can individually edit each event.");
  }

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onCancel}
      classNames={styles.confirm_cancel_modal}
      wrapperClassName="confirm_cancel"
    >
      <div className={styles.bill_now_modal_content}>
        <div className={styles.bill_now_content}>
          <i className="ico ico-dollar-circle" />
          <div className={styles.bill_now_text}>
            <h2>{title}</h2>
            <p>{message}</p>
          </div>
        </div>
        <div className={styles.bill_now_actions}>
          <ButtonVariant
            title={t("Ok")}
            buttonType="primary"
            buttonStyles={styles.bill_now_button}
            onClickFunc={onConfirm}
          />
          <ButtonVariant
            title={t("Cancel")}
            buttonType="light-primary"
            buttonStyles={styles.bill_now_button}
            onClickFunc={onCancel}
          />
        </div>
      </div>
    </ModalLayout>
  );
}
